import { easeInOut, motion } from 'motion/react'
import { Eye, Lock, Server, Shield } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Seo } from '~/components/Seo'
import { PageLayout } from '~/lib/components/layouts/PageLayout'

const floatingAnimation = {
  initial: { y: 0 },
  animate: {
    y: [-10, 10, -10],
    transition: {
      duration: 6,
      repeat: Infinity,
      ease: easeInOut,
    },
  },
}

const glowAnimation = {
  initial: { opacity: 0.3 },
  animate: {
    opacity: [0.3, 0.6, 0.3],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: easeInOut,
    },
  },
}

export default function Security() {
  const securityFeatures = [
    {
      icon: Shield,
      title: 'Data Encryption',
      description:
        'All data is encrypted in transit and at rest using industry-standard encryption protocols.',
    },
    {
      icon: Lock,
      title: 'Access Control',
      description:
        'Robust authentication and authorization systems to ensure data access security.',
    },
    {
      icon: Eye,
      title: 'Privacy Protection',
      description: 'Your research data is private and only accessible to authorized team members.',
    },
    {
      icon: Server,
      title: 'Secure Infrastructure',
      description:
        'Our infrastructure is hosted on secure cloud providers with regular security audits.',
    },
  ]

  return (
    <PageLayout>
      <Seo
        title="Security | SOTA Navigator"
        description="How SOTA Navigator protects your project data: hosting, encryption, access control, backups, and how we handle the documents you upload."
      />

      <div className="relative overflow-hidden bg-[#001871] text-white">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1584036561566-baf8f5f1b144?ixlib=rb-4.0.3')] opacity-5 mix-blend-soft-light"></div>
        <div className="absolute inset-0 bg-linear-to-b from-[#001871] via-[#001871] to-[#001871]/90"></div>

        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 0.15, scale: 1 }}
          transition={{ duration: 2 }}
          className="absolute left-20 top-20 size-96 rounded-full bg-white blur-3xl"
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 0.15, scale: 1 }}
          transition={{ duration: 2, delay: 0.5 }}
          className="absolute bottom-20 right-20 size-96 rounded-full bg-white blur-3xl"
        />

        <motion.div
          variants={floatingAnimation}
          initial="initial"
          animate="animate"
          className="absolute left-20 top-40 hidden size-20 rounded-2xl bg-white/20 shadow-lg backdrop-blur-lg lg:block"
        />
        <motion.div
          variants={floatingAnimation}
          initial="initial"
          animate="animate"
          transition={{ delay: 1 }}
          className="absolute bottom-40 right-20 hidden size-16 rounded-full bg-white/20 shadow-lg backdrop-blur-lg lg:block"
        />

        <div className="relative pb-40 pt-32 sm:pb-48 sm:pt-40">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="mx-auto max-w-4xl text-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
                className="space-y-8"
              >
                <motion.div
                  variants={glowAnimation}
                  initial="initial"
                  animate="animate"
                  className="inline-flex items-center gap-2 rounded-full bg-white/20 px-6 py-2 text-sm font-medium text-white shadow-lg backdrop-blur-xs"
                >
                  <Shield className="size-4" />
                  Enterprise-Grade Security
                </motion.div>

                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
                  className="text-5xl font-bold tracking-tight text-white [text-shadow:0_1px_2px_rgb(0_0_0/20%)] sm:text-7xl"
                >
                  Security First
                </motion.h1>

                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.3, ease: 'easeOut' }}
                  className="text-xl leading-8 text-white [text-shadow:0_1px_2px_rgb(0_0_0/20%)] sm:text-2xl"
                >
                  Your research data is protected by industry-leading security measures
                </motion.p>
              </motion.div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 w-full rotate-180 overflow-hidden leading-none">
          <svg
            className="relative block h-[50px] w-full"
            data-name="Layer 1"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
          >
            <path
              d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"
              className="fill-white"
            ></path>
          </svg>
        </div>
      </div>

      <section className="relative bg-white py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16 text-center"
          >
            <h2 className="mb-4 text-4xl font-bold tracking-tight text-[#001871] sm:text-5xl">
              Security Features
            </h2>
            <p className="text-xl text-gray-600">Your research data security is our top priority</p>
          </motion.div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
            {securityFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full border border-gray-100 bg-white transition-all duration-300 hover:shadow-xl">
                  <CardContent className="p-6">
                    <div className="flex flex-col items-center text-center">
                      <div className="mb-4 rounded-xl bg-[#001871]/5 p-4">
                        <feature.icon className="size-10 text-[#001871]" />
                      </div>
                      <h3 className="mb-2 text-xl font-semibold text-[#001871]">{feature.title}</h3>
                      <p className="text-gray-600">{feature.description}</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative bg-white py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg mx-auto max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="space-y-12"
            >
              <h2>Our Security Commitment</h2>
              <div className="space-y-6">
                <p className="text-lg text-gray-700">
                  At SOTA Navigator, security isn't just a feature—it's a fundamental principle that
                  guides everything we do. We've implemented a multi-layered security approach to
                  ensure your research data remains protected at all times.
                </p>

                <div className="space-y-8">
                  <div>
                    <h3 className="text-xl font-semibold text-[#001871]">
                      Data Protection & Recovery
                    </h3>
                    <ul className="mt-4 space-y-3">
                      <li className="flex items-start gap-3">
                        <span className="mt-1 shrink-0 text-[#001871]">•</span>
                        <span>
                          <strong>Encrypted Backups:</strong> All data is automatically backed up
                          with enterprise-grade encryption across multiple secure locations.
                        </span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="mt-1 shrink-0 text-[#001871]">•</span>
                        <span>
                          <strong>Disaster Recovery:</strong> We maintain a robust disaster recovery
                          plan with regular testing to ensure business continuity.
                        </span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="mt-1 shrink-0 text-[#001871]">•</span>
                        <span>
                          <strong>Access Controls:</strong> Strict access controls and
                          authentication mechanisms ensure your data is only accessible to
                          authorized personnel.
                        </span>
                      </li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-[#001871]">Security Response</h3>
                    <ul className="mt-4 space-y-3">
                      <li className="flex items-start gap-3">
                        <span className="mt-1 shrink-0 text-[#001871]">•</span>
                        <span>
                          <strong>Incident Response Team:</strong> Our dedicated security team is
                          available 24/7 to respond to and address any security concerns.
                        </span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="mt-1 shrink-0 text-[#001871]">•</span>
                        <span>
                          <strong>Regular Updates:</strong> We continuously update our security
                          measures to protect against emerging threats.
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="mt-12 rounded-xl bg-gray-50 p-6">
                <h2 className="text-2xl font-bold text-[#001871]">Reporting Security Issues</h2>
                <p className="mt-4 text-gray-700">
                  We take security vulnerabilities seriously and encourage responsible disclosure.
                  If you discover a potential security issue:
                </p>
                <ul className="mt-4 space-y-3">
                  <li className="flex items-start gap-3">
                    <span className="mt-1 shrink-0 text-[#001871]">1.</span>
                    <span>
                      Email our security team at{' '}
                      <a
                        href="mailto:security@sotanavigator.com"
                        className="font-medium text-[#001871] underline decoration-2 underline-offset-2 transition-colors hover:text-[#001871]/80"
                      >
                        security@sotanavigator.com
                      </a>
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="mt-1 shrink-0 text-[#001871]">2.</span>
                    <span>Include detailed information about the vulnerability</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="mt-1 shrink-0 text-[#001871]">3.</span>
                    <span>Our team will acknowledge your report within 24 hours</span>
                  </li>
                </ul>
                <p className="mt-4 text-sm text-gray-600">
                  We're committed to working with security researchers and rewarding those who help
                  us maintain the security of our platform.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </PageLayout>
  )
}
